import { useMemo } from 'react'
import DeckGL from '@deck.gl/react'
import { OrthographicView } from '@deck.gl/core'
import { BitmapLayer, LineLayer, PathLayer, PolygonLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers'
import type { LayerVisibility, MapCatalogEntry, MapManifest, Point2D, ReplayFrame, TrajectoryCandidate } from '../types'

type Color = [number, number, number, number]
type Position = [number, number]
interface Segment {
  source: Position
  target: Position
  color: Color
  width: number
  label?: string
}
interface PolygonDatum {
  polygon: Position[]
  color: Color
  label?: string
}
interface PathDatum {
  path: Position[]
  color: Color
  width: number
  label: string
}
interface PointDatum {
  position: Position
  color: Color
  radius: number
  label: string
}

const palette = {
  history: [94, 123, 150, 210] as Color,
  global: [91, 189, 255, 235] as Color,
  planned: [255, 193, 79, 245] as Color,
  executed: [82, 214, 173, 245] as Color,
  laser: [100, 231, 218, 105] as Color,
  infrared: [255, 117, 117, 220] as Color,
  sonar: [173, 125, 255, 220] as Color,
}

function positions(points: Point2D[]): Position[] {
  return points.map(({ x, y }) => [x, y])
}

function gridColor(kind: string, value: number): Color {
  const alpha = Math.round(18 + value * 85)
  if (kind === 'cost') return [255, 154, 66, alpha]
  if (kind === 'motion') return [177, 111, 255, alpha]
  return [74, 216, 202, alpha]
}

function candidateColor(candidate: TrajectoryCandidate): Color {
  if (candidate.selected) return [255, 214, 88, 245]
  const normalized = Math.min(1, candidate.cost)
  return [100 + Math.round(normalized * 140), 160 - Math.round(normalized * 80), 200 - Math.round(normalized * 100), 110]
}

function laserStatusColor(flag: number, intensity: number): Color {
  const alpha = Math.round(105 + Math.max(0, Math.min(1, intensity)) * 130)
  switch (flag) {
    case 0x10: return [255, 72, 72, 245]
    case 0x20: return [255, 135, 62, 235]
    case 0x30: return [255, 100, 62, 240]
    case 0x40: return [255, 193, 79, 235]
    case 0x50: return [255, 225, 100, 225]
    case 0x60: return [82, 214, 173, 225]
    default:
      if ((flag & 0x08) !== 0) return [82, 214, 173, 245]
      if ((flag & 0x04) !== 0) return [231, 116, 255, 245]
      if ((flag & 0x02) !== 0) return [255, 214, 88, 245]
      if ((flag & 0x01) !== 0) return [100, 231, 218, alpha]
      return [palette.laser[0], palette.laser[1], palette.laser[2], alpha]
  }
}

export function MapCanvas({
  frame,
  manifest,
  mapCatalogID,
  staticMap,
  visibility,
}: {
  frame: ReplayFrame
  manifest: MapManifest
  mapCatalogID?: string
  staticMap?: MapCatalogEntry
  visibility: LayerVisibility
}) {
  const staticGeometry = staticMap && staticMap.width > 0 && staticMap.height > 0 ? staticMap : undefined
  const mapGeometry = staticGeometry ?? manifest
  const mapYaw = staticGeometry?.originYaw ?? 0
  const mapXAxis: Position = [Math.cos(mapYaw) * mapGeometry.width, Math.sin(mapYaw) * mapGeometry.width]
  const mapYAxis: Position = [-Math.sin(mapYaw) * mapGeometry.height, Math.cos(mapYaw) * mapGeometry.height]
  const mapCorners: [Position, Position, Position, Position] = [
    [mapGeometry.origin.x, mapGeometry.origin.y],
    [mapGeometry.origin.x + mapYAxis[0], mapGeometry.origin.y + mapYAxis[1]],
    [mapGeometry.origin.x + mapXAxis[0] + mapYAxis[0], mapGeometry.origin.y + mapXAxis[1] + mapYAxis[1]],
    [mapGeometry.origin.x + mapXAxis[0], mapGeometry.origin.y + mapXAxis[1]],
  ]
  const layers = useMemo(() => {
    const result = []

    if (visibility.map) {
      const floor: PolygonDatum[] = [
        {
          polygon: [
            mapCorners[0],
            mapCorners[3],
            mapCorners[2],
            mapCorners[1],
          ],
          color: [13, 29, 44, 255],
          label: staticMap?.name ?? manifest.id,
        },
      ]
      const rooms: PolygonDatum[] =
        !staticMap && manifest.id === 'hospital-floor-01'
          ? [
              {
                polygon: [
                  [-14, 6],
                  [-7, 6],
                  [-7, 9],
                  [-14, 9],
                ],
                color: [26, 48, 62, 255],
                label: '诊室 A',
              },
              {
                polygon: [
                  [-5, 6],
                  [3, 6],
                  [3, 9],
                  [-5, 9],
                ],
                color: [26, 48, 62, 255],
                label: '诊室 B',
              },
              {
                polygon: [
                  [5, 6],
                  [14, 6],
                  [14, 9],
                  [5, 9],
                ],
                color: [26, 48, 62, 255],
                label: '候诊区',
              },
              {
                polygon: [
                  [-14, -9],
                  [-4, -9],
                  [-4, -5.7],
                  [-14, -5.7],
                ],
                color: [25, 45, 60, 255],
                label: '治疗区',
              },
              {
                polygon: [
                  [5, -9],
                  [14, -9],
                  [14, -5.7],
                  [5, -5.7],
                ],
                color: [25, 45, 60, 255],
                label: '药房',
              },
            ]
          : []
      result.push(
        new PolygonLayer<PolygonDatum>({
          id: 'map-floor',
          data: floor,
          getPolygon: (d) => d.polygon,
          getFillColor: (d) => d.color,
          getLineColor: [62, 97, 115, 255],
          lineWidthMinPixels: 2,
          stroked: true,
          pickable: true,
        }),
      )
      if (staticGeometry?.rasterAvailable) {
        result.push(
          new BitmapLayer({
            id: `map-raster-${staticGeometry.mapId}`,
            image: `/api/v1/maps/${staticGeometry.mapId}/raster?catalogId=${encodeURIComponent(staticGeometry.catalogId)}`,
            bounds: mapCorners,
            opacity: 0.72,
            pickable: true,
          }),
        )
      }
      result.push(
        new PolygonLayer<PolygonDatum>({
          id: 'map-rooms',
          data: rooms,
          getPolygon: (d) => d.polygon,
          getFillColor: (d) => d.color,
          getLineColor: [53, 84, 102, 255],
          lineWidthMinPixels: 1,
          stroked: true,
          pickable: true,
        }),
      )
    }

    for (const grid of frame.grids) {
      const key = grid.kind === 'cost' ? 'costGrid' : grid.kind === 'motion' ? 'motionGrid' : 'localGrid'
      if (!visibility[key]) continue
      const half = grid.resolution / 2
      const cells: PolygonDatum[] = grid.cells.map((cell) => ({
        polygon: [
          [cell.x - half, cell.y - half],
          [cell.x + half, cell.y - half],
          [cell.x + half, cell.y + half],
          [cell.x - half, cell.y + half],
        ],
        color: gridColor(grid.kind, cell.value),
        label: `${grid.kind}: ${cell.value.toFixed(2)}`,
      }))
      result.push(
        new PolygonLayer<PolygonDatum>({
          id: `grid-${grid.kind}`,
          data: cells,
          getPolygon: (d) => d.polygon,
          getFillColor: (d) => d.color,
          stroked: false,
          pickable: true,
        }),
      )
    }

    const pathData: PathDatum[] = []
    if (visibility.history) pathData.push({ path: positions(frame.historyPath.points), color: palette.history, width: 2, label: '历史轨迹' })
    if (visibility.globalPath) pathData.push({ path: positions(frame.globalPath.points), color: palette.global, width: 4, label: '全局路径' })
    if (visibility.localPlanned) pathData.push({ path: positions(frame.localPlannedPath.points), color: palette.planned, width: 5, label: '局部计划路径' })
    if (visibility.localExecuted) pathData.push({ path: positions(frame.localExecutedPath.points), color: palette.executed, width: 3, label: '局部执行路径' })
    if (pathData.length)
      result.push(
        new PathLayer<PathDatum>({
          id: 'paths',
          data: pathData,
          getPath: (d) => d.path,
          getColor: (d) => d.color,
          getWidth: (d) => d.width,
          widthUnits: 'pixels',
          jointRounded: true,
          capRounded: true,
          pickable: true,
        }),
      )

    const robot = frame.pose.pose
    if (visibility.trajectories) {
      const trajectories: PathDatum[] = frame.trajectories.map((candidate) => ({
        path: positions(candidate.points),
        color: candidateColor(candidate),
        width: candidate.selected ? 4 : 2,
        label: `${candidate.id} cost=${candidate.cost.toFixed(3)}`,
      }))
      result.push(
        new PathLayer<PathDatum>({
          id: 'trajectory-candidates',
          data: trajectories,
          getPath: (d) => d.path,
          getColor: (d) => d.color,
          getWidth: (d) => d.width,
          widthUnits: 'pixels',
          pickable: true,
        }),
      )
    }

    if (visibility.peerRobots) {
      const peers = frame.peerRobots.filter((peer) => peer.pose.mapId === 0 || robot.mapId === 0 || peer.pose.mapId === robot.mapId)
      const peerPaths: PathDatum[] = peers
        .filter((peer) => peer.plannedPath.length > 1)
        .map((peer) => ({
          path: positions(peer.plannedPath),
          color: peer.source === 'fusion' ? [255, 119, 200, 180] : [255, 157, 66, 155],
          width: 2,
          label: `协同机器人 ${peer.id} 规划路径`,
        }))
      if (peerPaths.length)
        result.push(
          new PathLayer<PathDatum>({
            id: 'peer-planned-paths',
            data: peerPaths,
            getPath: (d) => d.path,
            getColor: (d) => d.color,
            getWidth: (d) => d.width,
            widthUnits: 'pixels',
            pickable: true,
          }),
        )
      const peerPoints: PointDatum[] = peers.map((peer) => ({
        position: [peer.pose.x, peer.pose.y],
        color: peer.source === 'fusion' ? [255, 119, 200, 245] : [255, 157, 66, 235],
        radius: 0.28,
        label: `协同机器人 ${peer.id} · ${peer.source} · v=${peer.speedV.toFixed(2)}m/s`,
      }))
      result.push(
        new ScatterplotLayer<PointDatum>({
          id: 'peer-robots',
          data: peerPoints,
          getPosition: (d) => d.position,
          getFillColor: (d) => d.color,
          getRadius: (d) => d.radius,
          radiusUnits: 'meters',
          getLineColor: [255, 255, 255, 220],
          getLineWidth: 1.5,
          lineWidthUnits: 'pixels',
          stroked: true,
          pickable: true,
        }),
      )
      result.push(
        new TextLayer<PointDatum>({
          id: 'peer-labels',
          data: peerPoints,
          getPosition: (d) => d.position,
          getText: (d) => d.label.split(' · ')[0].replace('协同机器人 ', 'R'),
          getColor: (d) => d.color,
          getSize: 11,
          getPixelOffset: [0, -13],
          billboard: true,
        }),
      )
    }

    if (visibility.laser) {
      const laserPoints: PointDatum[] = []
      const laserOrigin = frame.laser.origin
      frame.laser.ranges.forEach((range, index) => {
        if (range <= 0) return
        const angle = laserOrigin.yaw + frame.laser.angleMin + index * frame.laser.angleIncrement
        const flag = frame.laser.flags?.[index] ?? 0
        laserPoints.push({
          position: [laserOrigin.x + Math.cos(angle) * range, laserOrigin.y + Math.sin(angle) * range],
          color: laserStatusColor(flag, frame.laser.intensities[index] ?? 0),
          radius: 0.035,
          label: `laser ${range.toFixed(2)}m · flag 0x${flag.toString(16).toUpperCase()}`,
        })
      })
      result.push(
        new ScatterplotLayer<PointDatum>({
          id: 'laser-returns',
          data: laserPoints,
          getPosition: (d) => d.position,
          getFillColor: (d) => d.color,
          getRadius: (d) => d.radius,
          radiusUnits: 'meters',
          radiusMinPixels: 1.2,
          radiusMaxPixels: 3,
          pickable: true,
        }),
      )
    }

    const sensorSegments: Segment[] = []
    for (const sensor of frame.rangeSensors) {
      if (!sensor.enabled || !visibility[sensor.kind]) continue
      const color: Color = sensor.healthy ? palette[sensor.kind] : [255, 78, 78, 255]
      sensorSegments.push({
        source: [sensor.pose.x, sensor.pose.y],
        target: [sensor.pose.x + Math.cos(sensor.pose.yaw) * sensor.distance, sensor.pose.y + Math.sin(sensor.pose.yaw) * sensor.distance],
        color,
        width: sensor.kind === 'sonar' ? 4 : 2,
        label: `${sensor.id} ${sensor.distance.toFixed(2)}m`,
      })
    }
    if (sensorSegments.length)
      result.push(
        new LineLayer<Segment>({
          id: 'sensor-rays',
          data: sensorSegments,
          getSourcePosition: (d) => d.source,
          getTargetPosition: (d) => d.target,
          getColor: (d) => d.color,
          getWidth: (d) => d.width,
          widthUnits: 'pixels',
          pickable: true,
        }),
      )

    const direction: Segment[] = [
      {
        source: [robot.x, robot.y],
        target: [robot.x + Math.cos(robot.yaw) * 0.85, robot.y + Math.sin(robot.yaw) * 0.85],
        color: [255, 255, 255, 255],
        width: 3,
      },
    ]
    result.push(
      new LineLayer<Segment>({
        id: 'robot-heading',
        data: direction,
        getSourcePosition: (d) => d.source,
        getTargetPosition: (d) => d.target,
        getColor: (d) => d.color,
        getWidth: (d) => d.width,
        widthUnits: 'pixels',
      }),
    )
    result.push(
      new ScatterplotLayer<PointDatum>({
        id: 'robot',
        data: [{ position: [robot.x, robot.y], color: [48, 221, 194, 255], radius: 0.38, label: '融合定位' }],
        getPosition: (d) => d.position,
        getFillColor: (d) => d.color,
        getLineColor: [238, 255, 252, 255],
        getRadius: (d) => d.radius,
        radiusUnits: 'meters',
        getLineWidth: 2,
        lineWidthUnits: 'pixels',
        stroked: true,
        pickable: true,
      }),
    )

    if (visibility.strategies) {
      const strategyColors: Record<string, Color> = {
        fused: [48, 221, 194, 255],
        encoder: [255, 157, 66, 240],
        rtslam: [111, 166, 255, 240],
        stm: [255, 214, 88, 240],
        encoder_rtslam: [85, 214, 190, 240],
        encoder_stm: [255, 140, 105, 240],
        rtslam_stm: [119, 191, 255, 240],
        encoder_rtslam_stm: [220, 132, 255, 240],
        encoder_rtslam_fusion: [255, 119, 200, 240],
        global: [255, 119, 200, 240],
        static_map: [176, 120, 255, 240],
        vio: [93, 224, 129, 240],
        ground_truth: [255, 214, 88, 240],
      }
      const strategyLabels: Record<string, string> = {
        encoder: '编码器',
        icp: 'ICP',
        map_match: '地图匹配',
        rtslam: 'RTSLAM',
        stm: 'STM',
        encoder_rtslam: '编码器 + RTSLAM',
        encoder_stm: '编码器 + STM',
        rtslam_stm: 'RTSLAM + STM',
        encoder_rtslam_stm: '编码器 + RTSLAM + STM',
        encoder_rtslam_fusion: '编码器 + RTSLAM 融合',
        global: '全局定位',
        static_map: '静态地图定位',
        vio: 'VIO',
        ground_truth: '真值',
        reset_global: '全局重定位',
        reset_rtslam: 'RTSLAM 重定位',
      }
      const points: PointDatum[] = frame.strategyPoses
        .filter((item) => item.strategy !== 'fused')
        .map((item) => ({
          position: [item.pose.x, item.pose.y],
          color: strategyColors[item.strategy] ?? [200, 200, 200, 220],
          radius: 0.22,
          label: strategyLabels[item.strategy] ?? item.strategy,
        }))
      const strategyPaths: PathDatum[] = frame.strategyPoses
        .filter((item) => (item.trail?.length ?? 0) > 1)
        .map((item) => ({
          path: positions(item.trail ?? []),
          color: strategyColors[item.strategy] ?? [200, 200, 200, 180],
          width: 1.5,
          label: `${strategyLabels[item.strategy] ?? item.strategy} 轨迹`,
        }))
      if (strategyPaths.length > 0) {
        result.push(
          new PathLayer<PathDatum>({
            id: 'strategy-history-paths',
            data: strategyPaths,
            getPath: (d) => d.path,
            getColor: (d) => d.color,
            getWidth: (d) => d.width,
            widthUnits: 'pixels',
            pickable: true,
          }),
        )
      }
      result.push(
        new ScatterplotLayer<PointDatum>({
          id: 'strategy-poses',
          data: points,
          getPosition: (d) => d.position,
          getFillColor: (d) => d.color,
          getRadius: (d) => d.radius,
          radiusUnits: 'meters',
          pickable: true,
        }),
      )
      result.push(
        new TextLayer<PointDatum>({
          id: 'strategy-labels',
          data: points,
          characterSet: 'auto',
          getPosition: (d) => d.position,
          getText: (d) => d.label,
          getColor: (d) => d.color,
          getSize: 12,
          getPixelOffset: [0, -14],
          billboard: true,
        }),
      )
    }

    return result
  }, [frame, manifest, mapCorners, mapGeometry, staticGeometry, staticMap, visibility])

  const mapCenter: Position = [
    mapGeometry.origin.x + mapXAxis[0] / 2 + mapYAxis[0] / 2,
    mapGeometry.origin.y + mapXAxis[1] / 2 + mapYAxis[1] / 2,
  ]
  const fitZoom = Math.max(2.5, Math.min(6.5, Math.log2(520 / Math.max(1, mapGeometry.width, mapGeometry.height))))

  return (
    <div className="map-canvas">
      <DeckGL
        key={staticMap ? `map-${staticMap.mapId}` : manifest.id}
        views={new OrthographicView({ id: 'robot-map', controller: true, flipY: false })}
        initialViewState={{ target: [mapCenter[0], mapCenter[1], 0], zoom: fitZoom, minZoom: 1.5, maxZoom: 9 }}
        layers={layers}
        getTooltip={({ object }) => (object?.label ? { text: object.label } : null)}
        controller
      />
      <div className="map-axis">
        <span>X →</span>
        <span>Y ↑</span>
        <span>map · meter</span>
      </div>
      {staticMap && (
        <div className="pointcloud-pose">
          Map {staticMap.mapId} · {staticMap.name} ·{' '}
          {staticGeometry
            ? `${staticMap.resolution.toFixed(2)}m/px · origin(${staticMap.origin.x.toFixed(2)}, ${staticMap.origin.y.toFixed(2)})`
            : '静态资产缺失'}
        </div>
      )}
      {!staticMap && frame.pose.pose.mapId > 0 && (
        <div className="pointcloud-pose">
          Map {frame.pose.pose.mapId} · {mapCatalogID ? `目录 ${mapCatalogID} 中无静态资产` : '当前会话未绑定静态地图目录'}
        </div>
      )}
      {visibility.laser && frame.laser.flags?.some((flag) => flag !== 0) && (
        <div className="laser-legend">
          <span><i className="laser-valid" />有效</span>
          <span><i className="laser-reflector" />反光</span>
          <span><i className="laser-human" />人形</span>
          <span><i className="laser-match" />地图匹配</span>
          <span><i className="laser-warning" />安全区</span>
          <span><i className="laser-danger" />危险</span>
        </div>
      )}
      <div className="map-watermark">2D OCCUPANCY MAP · LAST FRAME ≤ PLAYHEAD</div>
    </div>
  )
}
